<template>
	<div>
		<div class='app-file-upload' v-if="typeof avatar === 'string' && avatar.length">
			<div class="app-file-upload-list">
				<div class="app-file-upload-item">
					<div class='app-file-upload-preview'>
						<img :src="imageData">
					</div>
					<div class='app-file-upload-content'>
						<p class='app-file-upload-name'>{{$t('account.form.fileUpload.photo_is_used')}}</p>
					</div>
					<div class='app-file-upload-action'>
						<div class='app-file-upload-action-icon-delete' @click="$emit('remove-avatar')">
							<v-icon icon='delete' size='14' />
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class='app-file-upload' v-else-if="files.length">
			<div class="app-file-upload-list">
				<div class="app-file-upload-item" v-for="(file, idx) in files" :key="file.name">
					<div class='app-file-upload-preview'>
						<img :src="imageData" v-if="imageData.length > 0">
					</div>
					<div class='app-file-upload-content'>
						<p class='app-file-upload-name'>{{ file.name }}</p>
					</div>
					<div class='app-file-upload-action'>
						<div class='app-file-upload-action-icon-delete' @click="removeFile(idx)">
							<v-icon icon='delete' size='14' />
						</div>
					</div>
				</div>
			</div>
		</div>

		<div
			:class="{ 'app-file-upload': true, 'active': isDrag }"
			@dragover.prevent="handleDragOn"
			@dragleave.prevent="handleDragOff"
			@drop.prevent="handleDrop"
			v-else
		>
			<label class="app-file-upload-label" for="fileInput">
				<input id="fileInput" type="file" class="app-file-upload-input" @change="handleFilesInput" :accept='accept' />
				<div class='app-file-upload-wrap'>
					<div class='app-file-upload-icon'>
						<v-icon icon='image' size='12' />
					</div>
					<div class='app-file-upload-content'>
						<p class='app-file-upload-title'>{{title}}</p>
						<p class='app-file-upload-text'>{{$t('account.form.fileUpload.or_drag_to_download')}}</p>
					</div>
				</div>
			</label>
		</div>
		<p class='app-file-upload-description' v-if='hint'>{{hint}}</p>
	</div>
</template>

<script>
	export default {
		data: () => ({
			isDrag: false,
			files: [],
			errorMessage: null,
			imageData: ""
		}),
		props: {
			title: String,
			maxSize: Number,
			hint: String,
			accept: {
				type: [String, Boolean],
				default: false
			},
			avatar: [String, Object, File]
		},
		mounted() {
			if(this.avatar) {
				this.imageData = this.avatar;
			}
		},
		methods: {
			removeFile(index) {
				this.files.splice(index, 1);
				this.imageData = "";
				this.$emit("update-files", null);
			},
			handleDragOn() {
				this.isDrag = true;
			},
			handleDragOff() {
				this.isDrag = false;
			},
			handleDrop(e) {
				if(this.accept && !this.accept.split(',').find(el => el === e.dataTransfer.files[0].type)) {
					this.$store.commit('notification/errorMessage', {title: this.$t('account.form.message.errors.file_type_is_forbidden'), text: this.hint ?? null})
					return false;
				}
				if(this.maxSize) {
					if(e.dataTransfer.files[0].size < this.maxSize * 1024) {
						const files = [...this.files, ...e.dataTransfer.files];
						this.handleFiles(files);
					} else {
						this.$store.commit('notification/errorMessage', {title: this.$t('account.form.message.errors.limit_exceeded'), text: this.hint ?? null})
					}
				} else {
					const files = [...this.files, ...e.dataTransfer.files];
					this.handleFiles(files);
				}
				this.isDrag = false;
			},
			handleFilesInput(e) {
				if(this.accept && !this.accept.split(',').find(el => el === e.target.files[0].type)) {
					this.$store.commit('notification/errorMessage', {title: this.$t('account.form.message.errors.file_type_is_forbidden'), text: this.hint ?? null})
					return false;
				}
				if(this.maxSize) {
					if(e.target.files[0].size < this.maxSize * 1024) {
						const files = [...this.files, ...e.target.files];
						this.handleFiles(files);
					}
				} else {
					const files = [...this.files, ...e.target.files];
					this.handleFiles(files);
				}
			},
			handleFiles(files) {
				this.files = [...files];
				this.previewImage(this.files[0]);
				this.$emit("update-files", this.files[0])
			},
			imageExists(url, callback) {
				let img = new Image();
				img.onload = () => callback(true);
				img.onerror = () => callback(false);
				img.src = url;
			},
			previewImage(event) {
				if (event) {
					let reader = new FileReader();
					reader.onload = (e) => {
						this.imageExists(e.target.result, (exists) => {
							this.imageData = exists ? e.target.result : `data:image/svg+xml;base64,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`
						});
					};
					reader.readAsDataURL(event);
				}
			}
		},
		watch: {
			avatar() {
				if(!this.avatar) {
					this.files = [];
					this.errorMessage = null;
					this.imageData = "";
				}
				this.imageData = this.avatar;
			}
		}
	};
</script>

<style lang="scss">
	@import "@/styles/global/variables";
	@import "@/styles/global/mixins";

	.app-file-upload {
		width: 100%;
		min-height: 48px;
		border: 1px dashed rgba($cyprus, .15);
		transition: background-color 0.3s;
		@include minw( $grid-breakpoints-xs ) {
			width: 230px;
		}
		&.active {
			background-color: rgba($green-persian, .1);
		}
		&-input {
			display: none;
		}
		&-wrap {
			display: flex;
			align-items: center;
			padding: 8px 16px;
			cursor: pointer;
		}
		&-icon {
			flex-shrink: 0;
			margin-right: 16px;
			color: $green-persian;
		}
		&-title {
			font-size: 12px;
			font-weight: 400;
			color: $green-persian;
			margin-bottom: 4px;
		}
		&-text {
			font-size: 10px;
			font-weight: 400;
			color: rgba($black, .4);
		}
		&-description {
			font-size: 10px;
			font-weight: 400;
			color: rgba($black, .5);
			margin-top: 5px;
		}
		&-item {
			display: flex;
			align-items: center;
			padding: 6px;
		}
		&-preview {
			width: 34px;
			height: 34px;
			margin-right: 15px;
			flex-shrink: 0;
			img {
				width: 100%;
				height: 100%;
				object-fit: contain;
			}
		}
		&-content {
			flex-grow: 1;
			width: calc(100% - 49px - 34px);
		}
		&-name {
			font-size: 12px;
			font-weight: 400;
			word-wrap: break-word;
		}
		&-action {
			flex-shrink: 0;
			&-icon-delete {
				margin: 0 10px;
				color: rgba($black, .4);
				cursor: pointer;
				transition: color $transition;
				&:hover {
					color: rgba($black, .7);
				}
			}
		}
	}
</style>
